import { useEffect, useCallback } from 'react';
import type { ComboboxOption } from './Combobox.types';

interface UseComboboxKeyboardProps {
  isOpen: boolean;
  displayOptions: ComboboxOption[];
  focusedIndex: number;
  setFocusedIndex: (index: number) => void;
  openDropdown: () => void;
  closeDropdown: () => void;
  toggleOption: (option: ComboboxOption) => void;
  removeLastValue: () => void;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  inputRef: React.RefObject<HTMLInputElement | null>;
  listRef: React.RefObject<HTMLUListElement | null>;
}

export function useComboboxKeyboard({
  isOpen,
  displayOptions,
  focusedIndex,
  setFocusedIndex,
  openDropdown,
  closeDropdown,
  toggleOption,
  removeLastValue,
  searchTerm,
  setSearchTerm,
  inputRef,
  listRef,
}: UseComboboxKeyboardProps) {
  const scrollToOption = useCallback(
    (index: number) => {
      if (!listRef.current) return;
      const option = listRef.current.children[index] as HTMLElement | undefined;
      option?.scrollIntoView({ block: 'nearest' });
    },
    [listRef]
  );

  useEffect(() => {
    const input = inputRef.current;
    if (!input) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown': {
          e.preventDefault();
          if (!isOpen) {
            openDropdown();
            return;
          }
          const nextIndex =
            focusedIndex < displayOptions.length - 1 ? focusedIndex + 1 : 0;
          setFocusedIndex(nextIndex);
          scrollToOption(nextIndex);
          break;
        }

        case 'ArrowUp': {
          e.preventDefault();
          if (!isOpen) return;
          if (focusedIndex <= 0) {
            closeDropdown();
            return;
          }
          const prevIndex = focusedIndex - 1;
          setFocusedIndex(prevIndex);
          scrollToOption(prevIndex);
          break;
        }

        case 'Enter': {
          e.preventDefault();
          if (isOpen && focusedIndex >= 0 && focusedIndex < displayOptions.length) {
            toggleOption(displayOptions[focusedIndex]);
          }
          break;
        }

        case ' ': {
          // Only intercept Space when an option is focused in the dropdown
          if (isOpen && focusedIndex >= 0) {
            e.preventDefault();
            toggleOption(displayOptions[focusedIndex]);
          }
          break;
        }

        case 'Escape': {
          e.preventDefault();
          if (isOpen) {
            closeDropdown();
          } else if (searchTerm) {
            setSearchTerm('');
          }
          break;
        }

        case 'Tab': {
          if (isOpen) {
            closeDropdown();
          }
          break;
        }

        case 'Backspace': {
          if (searchTerm === '') {
            removeLastValue();
          }
          break;
        }
      }
    };

    input.addEventListener('keydown', handleKeyDown);
    return () => input.removeEventListener('keydown', handleKeyDown);
  }, [
    isOpen,
    displayOptions,
    focusedIndex,
    setFocusedIndex,
    openDropdown,
    closeDropdown,
    toggleOption,
    removeLastValue,
    searchTerm,
    setSearchTerm,
    inputRef,
    scrollToOption,
  ]);
}
